/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { licensedAssetsUrls } from '@/configs/urls'
import {
  getFileTypeIconUrl,
  normalizeValueArrayToString,
  setPreviewUrl
} from '@/utils/items'
import { createId } from '@/utils/general'
import { formatEmptyValue } from '@/utils/assets'
import { ASSET_LICENSING_BATCH_LABEL, MANAGE_LABEL } from '@/configs/strings/items-labels'
import { getBatchNames, getDefaultCardDetailsData } from '@/utils/cards.utils'

const useLicensedAssets = () => {
  const getCommonValues = (licensedAsset) => {
    const assetId = licensedAsset.assetId
    const earned = 0
    const licensedAssetUrl = `/${licensedAssetsUrls.base}/${licensedAsset.id}`
    const aiMetadataItems = licensedAsset.aiMetadata
    const isActive = aiMetadataItems > 0
    const activeLabel = isActive && `${aiMetadataItems} AI Metadata`
    const inactiveLabel = 'NO AI Metadata'
    const assetsLicensesBatch = getBatchNames({
      containerValue: licensedAsset.assetsLicensesBatch,
      batchNameKey: 'name'
    })
    const fileType = normalizeValueArrayToString(
      licensedAsset.fileType
    )
    const publishers = formatEmptyValue(
      normalizeValueArrayToString(licensedAsset.publishers)
    )
    const creators = formatEmptyValue(
      normalizeValueArrayToString(licensedAsset.creators)
    )
    const buttonLabel = MANAGE_LABEL
    const title = licensedAsset.title

    return ({
      activeLabel,
      assetId,
      assetsLicensesBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isActive,
      inactiveLabel,
      licensedAssetUrl,
      publishers,
      title
    })
  }
  const getCardProps = (licensedAsset) => {
    const {
      activeLabel,
      assetId,
      assetsLicensesBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isActive,
      inactiveLabel,
      licensedAssetUrl,
      publishers,
      title
    } = getCommonValues(licensedAsset)
    const details = [
      ...getDefaultCardDetailsData({
        publisherValue: publishers,
        fileTypeValue: fileType,
        assetIdValue: assetId
      }),
      {
        label: ASSET_LICENSING_BATCH_LABEL,
        value: assetsLicensesBatch
      },
      {
        value: `$ ${earned} earned`
      }
    ]
    return {
      activeLabel,
      assetId,
      buttonLabel,
      creators,
      details,
      imageUrl: setPreviewUrl(licensedAsset.coverUrl, fileType),
      inactiveLabel,
      isActive,
      key: createId(),
      licensedAssetUrl,
      title
    }
  }

  const getItemsPropsForTable = (licensedAsset) => {
    const {
      activeLabel,
      assetId,
      assetsLicensesBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isActive,
      inactiveLabel,
      licensedAssetUrl,
      publishers,
      title
    } = getCommonValues(licensedAsset)

    return {
      activeLabel,
      assetId,
      assetsLicensesBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      iconUrl: getFileTypeIconUrl(fileType),
      isActive,
      publishers,
      url: licensedAssetUrl,
      aiMetadata: activeLabel || inactiveLabel,
      title
    }
  }

  const tableColumns = [
    {
      accessorKey: 'icon',
      enableHiding: false,
      header: ''
    },
    {
      accessorKey: 'assetId',
      enableHiding: false,
      header: 'Asset Id'
    },
    {
      accessorKey: 'title',
      enableHiding: false,
      header: 'Title'
    },
    {
      accessorKey: 'earned',
      enableHiding: false,
      header: 'Earned'
    },
    {
      accessorKey: 'creators',
      header: 'Creators'
    },
    {
      accessorKey: 'publishers',
      header: 'Publishers'
    },
    {
      accessorKey: 'fileType',
      header: 'File Type'
    },
    {
      accessorKey: 'aiMetadata',
      header: 'AI Metadata'
    },
    {
      accessorKey: 'assetLicensingBatch',
      header: 'Asset Licensing Batch'
    },
    {
      accessorKey: 'actions',
      enableHiding: false,
      header: ''
    }
  ]

  const defaultHiddenColumns = [
    'creators',
    'publishers',
    'fileType',
    'aiMetadata',
    'assetLicensingBatch'
  ]

  const tableInitialState = {
    columnVisibility: Object.fromEntries(
      defaultHiddenColumns.map((id) => [id, false])
    )
  }

  return ({
    getCardProps,
    getItemsPropsForTable,
    tableColumns,
    tableInitialState
  })
}

export default useLicensedAssets
